<template>
  <div class="sj-range" :class="`sj-range-${theme}`">
    <div class="rangeLabel" v-if="showRange">{{ getLabel(range[0]) }}</div>
    <div class="main">
      <div class="handle">
        <div class="handle-bar" :style="{ backgroundColor: placeholderColor }">
          <!--<div class="circle" style="left: 0"></div>-->
          <!--<div class="circle" style="left: 100%"></div>-->
          <div
            class="handle-circle"
            :style="{ left: `${displayValue}%`, backgroundColor: color }"
            @mousedown.prevent="dragStart($event)"
          ></div>
        </div>
      </div>
      <div class="label-container">
        <div
          class="label"
          :class="{ show: showLabel || dragging }"
          :style="{ left: `${displayValue}%`, color: textColor }"
        >
          {{ getLabel(value) }}
        </div>
      </div>
    </div>
    <div class="rangeLabel" :style="{ color: textColor }" v-if="showRange">
      {{ getLabel(range[range.length - 1]) }}
    </div>
  </div>
</template>

<script>
/**
 * slider组件，用于在一个范围内选定一个值
 * 属性可参考range组件
 */
export default {
  name: "SjSlider",
  data() {
    return {
      dragging: false
    };
  },
  props: {
    range: {
      type: Array,
      // required: true,
      validator(v) {
        return (
          v instanceof Array &&
          typeof v[0] === "number" &&
          typeof v[1] === "number"
        );
      }
    },
    value: {
      type: Number,
      default: 0
    },
    step: {
      type: Number,
      default: 1
    },
    getLabel: {
      type: Function,
      default() {
        return null;
      }
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    showRange: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: "default"
    },
    /**
     * 选中柱形颜色
     */
    color: String,
    /**
     * 底柱形颜色
     */
    placeholderColor: String,
    /**
     * 文本颜色
     */
    textColor: String
  },
  computed: {
    displayValue() {
      if (this.range[1] - this.range[0] === 0) return 0;
      return (
        ((this.value - this.range[0]) / (this.range[1] - this.range[0])) * 100
      );
    }
  },
  methods: {
    dragStart(e) {
      this.dragging = true;
      window.addEventListener("mousemove", this.dragMove);
      window.addEventListener("mouseup", this.dragEnd);
      window.addEventListener("contextmenu", this.dragEnd);
      e.stopPropagation();
    },
    /**
     * 拖拽方法，在拖拽时增加了step属性的参与，且拖拽时上报input
     */
    dragMove(e) {
      let x = e.clientX;
      const area = this.getArea();
      if (x < area.left) {
        x = area.left;
      }
      if (x > area.right) {
        x = area.right;
      }
      const percentage = (x - area.left) / (area.right - area.left);
      const v =
        Math.round(
          (percentage * (this.range[1] - this.range[0]) + this.range[0]) /
            this.step
        ) * this.step;
      if (this.value !== v) {
        this.$emit("input", v);
      }
    },
    dragEnd() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.dragMove);
      window.removeEventListener("mouseup", this.dragEnd);
      window.removeEventListener("contextmenu", this.dragEnd);
      this.$emit("changed", this.value);
    },
    getArea() {
      const box = this.$el.querySelector(".handle-bar").getBoundingClientRect();
      return {
        left: box.left,
        right: box.right
      };
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (!this.range) return;
        if (value === null) {
          this.$emit("input", this.range[1]);
        } else {
          if (this.value < this.range[0]) {
            this.$emit("input", this.range[0]);
          }
          if (this.value > this.range[1]) {
            this.$emit("input", this.range[1]);
          }
        }
      }
    },
    range: {
      immediate: true,
      handler(range) {
        if (!this.range) return;
        if (this.value === null) {
          this.$emit("input", this.range[1]);
        } else {
          if (this.value < range[0]) {
            this.$emit("input", this.range[0]);
          }
          if (this.value > range[1]) {
            this.$emit("input", this.range[1]);
          }
        }
        // this.$emit("input", this.value);
      }
    }
  }
};
</script>

<style scoped lang="less">
.sj-range {
  display: flex;
  align-items: center;
  @primary-color: @color-available;
  @sub-color: fadeout(@color-available, 70%);
  &.sj-range-default {
    @bar-height: 2px;
    @circle-outer-size: 12px;
    @circle-inner-size: 8px;
    .handle-bar {
      height: @bar-height;
      border-radius: @bar-height;
    }
    .handle-circle {
      top: calc((@bar-height - @circle-outer-size) / 2);
      width: @circle-outer-size;
      height: @circle-outer-size;
      &::after {
        top: calc(50% - @circle-inner-size / 2);
        left: calc(50% - @circle-inner-size / 2);
        width: @circle-inner-size;
        height: @circle-inner-size;
      }
    }
  }
  &.sj-range-fill {
    @bar-height: 4px;
    @circle-outer-size: 15px;
    @circle-inner-size: 0px;
    .handle-bar {
      height: @bar-height;
      border-radius: @bar-height;
    }
    .handle-circle {
      top: calc((@bar-height - @circle-outer-size) / 2);
      width: @circle-outer-size;
      height: @circle-outer-size;
      &::after {
        top: calc(50% - @circle-inner-size / 2);
        left: calc(50% - @circle-inner-size / 2);
        width: @circle-inner-size;
        height: @circle-inner-size;
      }
    }
  }

  .rangeLabel {
    padding: 0 4px;
    flex-shrink: 0;
    .text("caption", "subinfo");
  }

  .main {
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    align-items: center;
    padding: 5px;
    height: 40px;
    box-sizing: border-box;

    .panel {
      flex-shrink: 0;
    }

    .handle {
      flex-grow: 1;
      position: relative;
      padding: 0 20px;
    }

    .handle-bar {
      position: relative;
      width: 100%;
      background-color: @sub-color;
    }
    .handle-circle {
      position: absolute;
      border-radius: 50%;
      box-sizing: border-box;
      transform: translateX(-50%);
      &::after {
        content: "";
        position: absolute;
        top: calc(50% - var(--circle-inner-size) / 2);
        left: calc(50% - var(--circle-inner-size) / 2);
        width: var(--circle-inner-size);
        height: var(--circle-inner-size);
        border-radius: 50%;
        box-sizing: border-box;
        background-color: @color-antibg;
      }
      background-color: @primary-color;
      cursor: pointer;
    }

    .center-bar {
      position: relative;
      height: 2px;
      background-color: @primary-color;
    }
  }
  .label-container {
    top: -5px;
    left: 0;
    right: 0;
    position: absolute;
    margin: 0 26px;
    pointer-events: none;
    /*height: 20px;*/
    .label {
      .text("caption", "dark", "regular");
      position: absolute;
      transform: translateX(-50%);
      transition: opacity 0.1s ease;
      opacity: 0;
      word-break: keep-all;
      white-space: nowrap;
    }
    .show {
      opacity: 1;
    }
  }
}
</style>
